import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onboardingAssetUrl } from './wizardApi';
import DreamMarque from '../common/DreamMarque';
import { ReactComponent as DreamWordmark } from '../../icons/brand/dream-wordmark.svg';

export function assistantEmail(fullName: string): string {
  const slug =
    fullName
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .join('.')
      .replace(/[^a-z0-9.]/g, '') || 'assistant';
  return `${slug}@dream-pa.net`;
}

interface NameStepProps {
  eyebrow: string;
  name: string;
  onNameChange: (name: string) => void;
  showValidation?: boolean;
}

export const AssistantNameStep: React.FC<NameStepProps> = ({
  eyebrow,
  name,
  onNameChange,
  showValidation = false,
}) => {
  const { t } = useTranslation();
  return (
    <section className='screen centred active' data-screen='assistant-name'>
      <div className='eyebrow'>{eyebrow}</div>
      <h1 className='ob-title'>{t('wizard.assistant.nameTitle')}</h1>
      <p className='ob-lede wide-only'>{t('wizard.assistant.nameLedeWide')}</p>
      <p className='ob-lede narrow-only'>
        {t('wizard.assistant.nameLedeNarrow')}
      </p>

      <div className='hero-stack'>
        <div className='identity'>
          <div className='avatar-lg'>
            {(name.trim() || 'D').charAt(0).toUpperCase()}
          </div>
          <div className='identity-field'>
            <label htmlFor='assistant-name'>
              {t('wizard.assistant.nameLabel')}
            </label>
            <input
              className='text-input'
              id='assistant-name'
              type='text'
              value={name}
              maxLength={20}
              autoComplete='off'
              aria-invalid={showValidation}
              aria-describedby='assistant-name-validation'
              onChange={(e) => onNameChange(e.target.value)}
            />
          </div>
        </div>
        <div
          id='assistant-name-validation'
          className={`validation${showValidation ? ' show' : ''}`}
          role='alert'
        >
          <svg
            viewBox='0 0 16 16'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <circle cx='8' cy='8' r='6.25' />
            <path d='M8 5v3.5M8 11h.01' />
          </svg>
          {t('wizard.assistant.nameRequired')}
        </div>
      </div>
    </section>
  );
};

interface EmailStepProps {
  eyebrow: string;
  name: string;
  fullName: string;
  email: string;
  userName: string;
}

export const AssistantEmailStep: React.FC<EmailStepProps> = ({
  eyebrow,
  name,
  fullName,
  email,
  userName,
}) => {
  const { t } = useTranslation();
  return (
    <section className='screen centred active' data-screen='assistant-email'>
      <div className='eyebrow'>{eyebrow}</div>
      <h1 className='ob-title'>{t('wizard.assistant.emailTitle', { name })}</h1>
      <p className='ob-lede'>{t('wizard.assistant.emailLede')}</p>

      <div className='hero-stack'>
        <div className='sig'>
          <p className='sig-quote'>{t('wizard.assistant.sigQuote')}</p>
          <div className='sig-block'>
            <span className='sig-rule'></span>
            <div>
              <div className='sig-name a-name'>{fullName}</div>
              <div className='sig-role'>
                {userName
                  ? t('wizard.assistant.sigRoleTo', { name: userName })
                  : t('wizard.assistant.sigRole')}
              </div>
              <div className='sig-meta'>
                <span className='a-email'>{email}</span>
              </div>
            </div>
          </div>
        </div>

        <div className='control'>
          <label className='control-label' htmlFor='email-perm'>
            {t('wizard.assistant.whenEmail')}
          </label>
          <div className='select-wrap'>
            <select
              className='perm-select'
              id='email-perm'
              defaultValue='express'
            >
              <option value='express'>
                {t('wizard.assistant.expressPermission')}
              </option>
            </select>
            <svg
              viewBox='0 0 16 16'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.75'
              aria-hidden='true'
            >
              <path
                d='M4 6.5L8 10.5l4-4'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

interface CardStepProps {
  eyebrow: string;
  fullName: string;
}

export const AssistantCardStep: React.FC<CardStepProps> = ({
  eyebrow,
  fullName,
}) => {
  const { t } = useTranslation();
  return (
    <section className='screen centred active' data-screen='assistant-card'>
      <div className='eyebrow'>{eyebrow}</div>
      <h1 className='ob-title'>{t('wizard.assistant.cardTitle')}</h1>
      <p className='ob-lede'>{t('wizard.assistant.cardLede')}</p>

      <div className='hero-stack'>
        <div className='bankcard'>
          <div className='bankcard-top'>
            <span className='bankcard-kind'>
              {t('wizard.assistant.cardKind')}
            </span>
            <span className='bankcard-logo' aria-hidden='true'>
              <DreamMarque
                variant='optical-md'
                className='bankcard-logo-marque'
              />
              <DreamWordmark className='bankcard-logo-wordmark' />
            </span>
          </div>
          <div>
            <div className='bankcard-chip'></div>
            <div className='bankcard-num'>•••• •••• •••• 4429</div>
          </div>
          <div className='bankcard-foot'>
            <div>
              <div className='bankcard-label'>
                {t('wizard.assistant.cardholder')}
              </div>
              <div className='bankcard-value a-cardname'>{fullName}</div>
            </div>
            <div>
              <div className='bankcard-label'>
                {t('wizard.assistant.expires')}
              </div>
              <div className='bankcard-value'>09/29</div>
            </div>
            <div className='bankcard-brand'>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <div className='control'>
          <label className='control-label' htmlFor='card-perm'>
            {t('wizard.assistant.whenSpend')}
          </label>
          <div className='select-wrap'>
            <select
              className='perm-select'
              id='card-perm'
              defaultValue='express'
            >
              <option value='express'>
                {t('wizard.assistant.expressPermission')}
              </option>
            </select>
            <svg
              viewBox='0 0 16 16'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.75'
              aria-hidden='true'
            >
              <path
                d='M4 6.5L8 10.5l4-4'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

const WAVE_BARS = 44;

const SAMPLE_CALL_LANGUAGES = ['en', 'de', 'fr', 'it', 'es'] as const;
type SampleCallLanguage = (typeof SAMPLE_CALL_LANGUAGES)[number];

function isSampleCallLanguage(lang: string): lang is SampleCallLanguage {
  return (SAMPLE_CALL_LANGUAGES as readonly string[]).includes(lang);
}

/** The user's UI language leads; the rest keep their fixed order. */
export function orderSampleCallLanguages(
  uiLanguage: string | undefined,
): SampleCallLanguage[] {
  const base = (uiLanguage ?? 'en').split('-')[0].toLowerCase();
  const first: SampleCallLanguage = isSampleCallLanguage(base) ? base : 'en';
  return [first, ...SAMPLE_CALL_LANGUAGES.filter((l) => l !== first)];
}

function sampleCallAsset(lang: SampleCallLanguage): string {
  return lang === 'en'
    ? 'booking-call-sample.ogg'
    : `booking-call-sample-${lang}.ogg`;
}

interface TeamStepProps {
  eyebrow: string;
  name: string;
  title?: string;
  lede?: string;
  hideList?: boolean;
}

export const TeamStep: React.FC<TeamStepProps> = ({
  eyebrow,
  name,
  title,
  lede,
  hideList,
}) => {
  const { t, i18n } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const languages = orderSampleCallLanguages(i18n.resolvedLanguage);
  const [sampleLang, setSampleLang] = useState<SampleCallLanguage>(
    languages[0],
  );

  const selectLanguage = (lang: SampleCallLanguage) => {
    if (lang === sampleLang) {
      return;
    }
    audioRef.current?.pause();
    setPlaying(false);
    setElapsed(0);
    setDuration(0);
    setSampleLang(lang);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    const onTime = () => setElapsed(audio.currentTime);
    const onMeta = () =>
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const onEnded = () => {
      setPlaying(false);
      setElapsed(0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnded);
    };
  }, [sampleLang]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  const fmt = (s: number) =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  const progress = duration > 0 ? elapsed / duration : 0;

  return (
    <section className='screen active' data-screen='team'>
      <div className='eyebrow'>{eyebrow}</div>
      <h1 className='ob-title'>
        {title ?? t('wizard.assistant.teamTitle', { name })}
      </h1>
      {lede ? (
        <p className='ob-lede'>{lede}</p>
      ) : (
        <>
          <p className='ob-lede wide-only'>
            {t('wizard.assistant.teamLedeWide', { name })}
          </p>
          <p className='ob-lede narrow-only'>
            {t('wizard.assistant.teamLedeNarrow', { name })}
          </p>
        </>
      )}

      <div className='team-card'>
        {!hideList && (
          <>
            <div className='perm-title'>{t('wizard.assistant.whatTheyDo')}</div>
            <ul className='team-list'>
              <li>
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.7'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M6.5 3.5h3l1.5 4-2 1.5a12 12 0 006 6l1.5-2 4 1.5v3a2 2 0 01-2.2 2A17.5 17.5 0 014.5 5.7a2 2 0 012-2.2z' />
                </svg>
                {t('wizard.assistant.teamItem1')}
              </li>
              <li>
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.7'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M4 5.5h16v13H4z' />
                  <path d='M4 9h16M9 18.5v-9' />
                </svg>
                {t('wizard.assistant.teamItem2')}
              </li>
              <li>
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.7'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <circle cx='12' cy='12' r='8.5' />
                  <path d='M12 7.5v5l3 1.8' />
                </svg>
                {t('wizard.assistant.teamItem3')}
              </li>
            </ul>
          </>
        )}

        <div className='audio-card'>
          <div className='audio-head'>
            <span className='audio-langs-label' id='audio-langs-label'>
              {t('wizard.assistant.hearCallIn')}
            </span>
            <div
              className='audio-tabs'
              role='tablist'
              aria-labelledby='audio-langs-label'
            >
              {languages.map((lang) => (
                <button
                  key={lang}
                  type='button'
                  role='tab'
                  aria-selected={lang === sampleLang}
                  className={`audio-tab${lang === sampleLang ? ' audio-tab--active' : ''}`}
                  onClick={() => selectLanguage(lang)}
                >
                  {t(`wizard.assistant.callLanguage.${lang}`)}
                </button>
              ))}
            </div>
          </div>

          <div className='audio'>
            <audio
              key={sampleLang}
              ref={audioRef}
              src={onboardingAssetUrl(sampleCallAsset(sampleLang))}
              preload='metadata'
            />
            <button
              className='audio-play'
              aria-label={
                playing
                  ? t('wizard.assistant.pauseSample')
                  : t('wizard.assistant.playSample')
              }
              onClick={togglePlay}
            >
              <svg viewBox='0 0 16 16' fill='currentColor' aria-hidden='true'>
                {playing ? (
                  <path d='M4.5 2.5h2.6v11H4.5zM8.9 2.5h2.6v11H8.9z' />
                ) : (
                  <path d='M4.5 2.8v10.4a.6.6 0 00.92.5l8.2-5.2a.6.6 0 000-1L5.42 2.3a.6.6 0 00-.92.5z' />
                )}
              </svg>
            </button>
            <div className='audio-main'>
              <div className='audio-title'>
                {t('wizard.assistant.audioTitle')}
              </div>
              <div className='wave' aria-hidden='true'>
                {Array.from({ length: WAVE_BARS }, (_, i) => (
                  <i
                    key={i}
                    className={i / WAVE_BARS <= progress ? 'on' : ''}
                    style={{
                      height: `${6 + Math.round(Math.abs(Math.sin(i * 1.7)) * 18)}px`,
                    }}
                  />
                ))}
              </div>
            </div>
            <span className='audio-time'>
              {fmt(elapsed)} / {fmt(duration)}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
